import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut as amplifySignOut,
  fetchAuthSession
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { rolesFromGroups, type Role } from '../auth/roles';

interface User {
  username: string;
  email: string;
  emailVerified: boolean;
  mfaEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  // From the ID token, so a change made on the Users page shows up at the
  // next token refresh (up to 15 minutes) unless refreshRoles() is called.
  roles: Role[];
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    checkUser();
  }, []);

  // Every token refresh (expiry, or a forced refresh after a 403) carries the
  // current groups, so the navigation follows a role change without a reload.
  // A failed refresh (e.g. the user was disabled) or a sign-out drops them.
  useEffect(
    () =>
      Hub.listen('auth', ({ payload }) => {
        // Not on tokenRefresh_failure: a network blip would swap the page for NoRoleAssigned.
        if (payload.event === 'signedOut') {
          setRoles([]);
          return;
        }
        if (payload.event !== 'tokenRefresh') return;
        fetchAuthSession()
          .then((session) => setRoles(rolesFromGroups(session.tokens?.idToken?.payload['cognito:groups'])))
          .catch(() => {});
      }),
    []
  );

  const checkUser = async () => {
    try {
      // Check if we're returning from OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      // Fetch the current session (handles OAuth code exchange automatically)
      const session = await fetchAuthSession();

      // If we have a valid session, get user details
      if (session.tokens) {
        const currentUser = await getCurrentUser();
        setRoles(rolesFromGroups(session.tokens.idToken?.payload['cognito:groups']));

        setUser({
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId || '',
          emailVerified: true,
          mfaEnabled: false
        });

        // Clean up URL if we came from OAuth callback
        if (code) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  const refreshRoles = useCallback(async () => {
    const session = await fetchAuthSession({ forceRefresh: true });
    setRoles(rolesFromGroups(session.tokens?.idToken?.payload['cognito:groups']));
  }, []);

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading: loading || !authChecked,
    signIn: handleSignIn,
    signOut: handleSignOut,
    isAuthenticated: !!user && authChecked,
    roles,
    refreshRoles
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
